import HotelStatusIcon from 'components/HotelStatusIcon'
import { PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import ArrowDown from '../../../../components/ReusableComponents/Icons/ArrowDown'
import ArrowUp from '../../../../components/ReusableComponents/Icons/ArrowUp'
import DotIcon from '../../../../components/ReusableComponents/Icons/Dot'
import { Cell, Row } from '../../../../components/ReusableComponents/Table'
import { formatPrice } from '../../../../utils/common'
import CampaignCheckCell from '../../AutoInvite/CheckCell'
import CustomTooltip from '../../AutoInvite/Row/Tooltip'
import CheckCell from '../../Common/CheckCell'
import HotelIdCell from '../../Common/HotelIdCell'
import HotelNameCell from '../../Common/HotelNameCell'
import useNote from '../../Common/useNote'
import { useNoteBatchLoader } from '../../Common/NoteBatchLoaderContext'
import CampaignStatusCell from '../CampaignStatusCell'
import styles from './Row.module.scss'

const NumberStatCell = ({ value, ...props }) => {
  const [isHovering, setHovering] = useState()

  return (
    <Cell className={styles.numberStatCell} {...props}>
      <div onMouseOver={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
        {value?.isIncreasing === true && <ArrowUp />}
        {value?.isIncreasing === false && <ArrowDown />}
        {value?.isIncreasing == null && <DotIcon />}

        {value?.periodCurrent}
        <CustomTooltip {...value} isVisible={isHovering} />
      </div>
    </Cell>
  )
}

const SummaryRow = ({
  displayId,
  brand,
  shortName,
  subsidiaryId,
  csmUserFullName,
  pms,
  isAutoPayEnabled,
  balanceCurrent,
  balanceLate,
  marketingCampaigns,
  index,
  subsidiaryStatus,
  invited,
  conversions,
  outstandingInvoiceCount,
  hasDripCampaign,
  customCampaignsCount,
  serviceStartDate,
  actualServiceStartDate,
  campaigns,
  rowStatus,
  statusExplanation
}) => {
  const { registerCallback, notesCache } = useNoteBatchLoader()
  const {
    note,
    setNote,
    isLoading: isLoadingNote,
    isSaving,
    error: noteError,
    loadNoteOnDemand,
    setNoteFromBatch
  } = useNote(subsidiaryId, 'healthCheck-summary', { batchLoad: true })

  // Register callback to receive batch-loaded notes
  useEffect(() => {
    const key = `${subsidiaryId}-healthCheck-summary`
    
    // Check if note is already in cache
    if (notesCache.has(key)) {
      setNoteFromBatch(notesCache.get(key))
    }

    // Register callback for future updates
    const unregister = registerCallback(subsidiaryId, 'healthCheck-summary', (data) => {
      setNoteFromBatch(data)
    })

    return unregister
  }, [subsidiaryId, registerCallback, notesCache, setNoteFromBatch])
  return (
    <div
      className={`${styles.rowHolder} ${rowStatus ? styles[rowStatus] : ''}`}
      title={statusExplanation}
    >
      <div className={styles.index}>{index}</div>
      <div className={styles.statusIcon}>
        <HotelStatusIcon status={subsidiaryStatus} />
      </div>
      <Row>
        <HotelIdCell displayId={displayId} />
        <HotelNameCell
          brand={brand}
          name={shortName}
          serviceStartDate={serviceStartDate}
          actualServiceStartDate={actualServiceStartDate}
          subsidiaryId={subsidiaryId}
          navigationControl={
            <Link to={`/s/${subsidiaryId}/hotelDetails`}>
              <button>Go to hotel details →</button>
            </Link>
          }
        />
        <Cell className={styles.textCell}>
          <span title={csmUserFullName}>
            {csmUserFullName
              ?.split(' ')
              .map(str => str[0])
              .join('.')}
          </span>
        </Cell>
        <Cell className={styles.textCell}>
          <span title={PMSnames[pms] || pms}>
            {(() => {
              const pmsName = PMSnames[pms] || pms
              // Show initials: if name has spaces, show first letter of each word, otherwise show first letter
              if (pmsName && pmsName.includes(' ')) {
                return pmsName
                  .split(' ')
                  .map(str => str[0])
                  .join('.')
              }
              return pmsName ? pmsName[0].toUpperCase() : pms ? pms[0].toUpperCase() : ''
            })()}
          </span>
        </Cell>
        <CheckCell isChecked={isAutoPayEnabled} />
        <CampaignCheckCell
          campaign={marketingCampaigns?.find(
            campaign => campaign.category === 'Lost3Months'
          )}
        />
        <CampaignCheckCell
          campaign={marketingCampaigns?.find(
            campaign => campaign.category === 'Lost6Months'
          )}
        />
        <CampaignCheckCell
          campaign={marketingCampaigns?.find(
            campaign => campaign.category === 'Lost9Months'
          )}
        />
        <CampaignCheckCell
          campaign={marketingCampaigns?.find(
            campaign => campaign.category === 'Lost12Months'
          )}
        />
        <CampaignCheckCell
          campaign={marketingCampaigns?.find(
            campaign => campaign.category === 'Lost15Months'
          )}
        />
        <CampaignCheckCell
          campaign={marketingCampaigns?.find(
            campaign => campaign.category === 'Lost15MonthsPlus'
          )}
        />
        <CampaignStatusCell
          campaigns={campaigns}
          kind="Sales"
          subsidiaryId={subsidiaryId}
        />
        <Cell className={styles.textCell} style={{ justifyContent: 'center' }}>
          {customCampaignsCount > 0 ? customCampaignsCount : '0'}
        </Cell>
        <CampaignStatusCell
          campaigns={campaigns}
          kind="Service"
          subsidiaryId={subsidiaryId}
        />
        <CampaignCheckCell
          campaign={marketingCampaigns?.find(
            campaign => campaign.category === 'AfterLastVisit'
          )}
        />
        <NumberStatCell value={invited} />
        <NumberStatCell value={conversions} />
        <Cell className={styles.textCell} style={{ justifyContent: 'center' }}>
          {formatPrice(balanceLate)}
        </Cell>
        <Cell className={styles.textCell} style={{ justifyContent: 'center' }}>
          {outstandingInvoiceCount || 0}
        </Cell>
        <Cell className={styles.noteCell}>
          <input
            type="text"
            value={note || ''}
            onChange={e => setNote(e.target.value)}
            onFocus={loadNoteOnDemand}
            placeholder=" "
            disabled={isLoadingNote}
          />
          {noteError && <div className={styles.noteError}>{noteError}</div>}
        </Cell>
      </Row>
    </div>
  )
}

export default SummaryRow
